/**
 * Cross-tab upload ownership via the Web Locks API.
 *
 * Problem: when a user starts an upload in Tab A and then duplicates the tab to Tab B,
 * Tab B's React app rehydrates the package from IndexedDB, sees CHUNK_UPLOAD_IN_PROGRESS
 * without an in-memory `tus.Upload`, and renders a "paused" card. Pressing Pause/Resume/
 * Cancel in Tab B would race with the actually-running upload in Tab A.
 *
 * Solution: each tab that owns an upload holds a Web Lock named `upload:<uploadId>` for
 * the lifetime of that upload. Other tabs can query the lock state via `navigator.locks`
 * to discover whether an upload is owned-by-me / owned-by-other / unowned and render UI
 * accordingly. Lock is released automatically when the owning tab closes or crashes.
 *
 * Browser support: Chrome 69+, Firefox 96+, Safari 15.4+, Edge 79+. Older browsers
 * silently fall back to "unsupported" ownership state, which the UI treats as "unknown".
 */

const LOCK_PREFIX = "clipn-upload:";

const isLocksSupported = () =>
  typeof navigator !== "undefined" &&
  navigator.locks &&
  typeof navigator.locks.request === "function" &&
  typeof navigator.locks.query === "function";

export const isWebLocksSupported = isLocksSupported;

/**
 * Local ownership is tracked only in the currently-running tab.
 *
 * We intentionally do NOT infer self-vs-other via a cross-tab tabId embedded in lock names.
 * That regression depended on duplicated tabs always parsing a fresh bundle; in practice a
 * browser can duplicate a page by copying the running JS context, which also copies module
 * scope. When that happened both tabs shared the same tabId and the duplicate misread the
 * owner's marker lock as its own.
 *
 * The UI only needs to know:
 *   - do WE currently hold the session lock?
 *   - do WE currently hold the presence lock?
 *   - does SOME tab hold either lock?
 *
 * That is enough to distinguish "me" / "other" / "none" without any cross-tab identity.
 */
const ownedUploadIds = new Set();
const ownedPresenceUploadIds = new Set();

// Active session ownership only. Broadcast mirroring must stay tied to the tab that is
// actually running the TUS session, not a passive presence claim after reload.
export const isOwnedByThisTab = (uploadId) => ownedUploadIds.has(uploadId);
export const isPresenceClaimedByThisTab = (uploadId) =>
  ownedPresenceUploadIds.has(uploadId);
export const isLocallyOwnedUpload = (uploadId) =>
  ownedUploadIds.has(uploadId) || ownedPresenceUploadIds.has(uploadId);

const markOwnedByThisTab = (uploadId) => {
  if (uploadId) ownedUploadIds.add(uploadId);
};

const markReleasedByThisTab = (uploadId) => {
  if (uploadId) ownedUploadIds.delete(uploadId);
};

const markPresenceClaimedByThisTab = (uploadId) => {
  if (uploadId) ownedPresenceUploadIds.add(uploadId);
};

const markPresenceReleasedByThisTab = (uploadId) => {
  if (uploadId) ownedPresenceUploadIds.delete(uploadId);
};

const lockName = (uploadId) => `${LOCK_PREFIX}${uploadId}`;

/**
 * Acquire an exclusive lock for an upload. Returns a release function when acquired.
 * If another tab already owns the upload, resolves to null instead of queueing.
 *
 * Uses `ifAvailable: true` on purpose. Queueing behind a live owner would silently transfer
 * control later, which is the opposite of the product semantics: a secondary tab must not
 * arm a pending takeover while another live tab is still managing the upload.
 */
export const acquireUploadLock = async (uploadId) => {
  if (!isLocksSupported() || !uploadId) {
    markOwnedByThisTab(uploadId);
    return () => markReleasedByThisTab(uploadId);
  }

  return new Promise((resolve, reject) => {
    let releaseInner = () => {};
    let released = false;

    const release = () => {
      if (released) return;
      released = true;
      markReleasedByThisTab(uploadId);
      try {
        releaseInner();
      } catch (_) {
        /* ignore */
      }
    };

    navigator.locks
      .request(
        lockName(uploadId),
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          if (!lock) {
            resolve(null);
            return;
          }
          markOwnedByThisTab(uploadId);
          resolve(release);
          await new Promise((innerResolve) => {
            releaseInner = innerResolve;
          });
          markReleasedByThisTab(uploadId);
        }
      )
      .catch((err) => {
        release();
        console.warn("[uploadOwnership] lock request failed", uploadId, err);
        reject(err);
      });
  });
};

/**
 * Silently claim the "presence" lock for an upload. Presence signals "some tab is here and
 * intends to be the main for this upload" without committing to an active TUS session.
 * Used by ResumePreviousUploadBanner on rehydration so a reloaded original tab can re-claim
 * ownership before a lingering duplicate tab misreads the lock release as a real orphan.
 */
export const claimUploadPresence = async (uploadId) => {
  if (!isLocksSupported() || !uploadId) return () => {};

  try {
    const snapshot = await navigator.locks.query();
    const held = snapshot.held || [];
    if (held.some((l) => l.name === lockName(uploadId))) return () => {};
  } catch {
    /* if query fails, proceed with best-effort claim */
  }

  const presenceName = `${lockName(uploadId)}::presence`;

  return new Promise((resolve, reject) => {
    let releasePresenceInner = () => {};
    let released = false;

    const releasePresence = () => {
      if (released) return;
      released = true;
      markPresenceReleasedByThisTab(uploadId);
      try {
        releasePresenceInner();
      } catch (_) {
        /* ignore */
      }
    };

    navigator.locks
      .request(presenceName, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        if (!lock) {
          resolve(() => {});
          return;
        }
        markPresenceClaimedByThisTab(uploadId);
        resolve(releasePresence);
        await new Promise((innerResolve) => {
          releasePresenceInner = innerResolve;
        });
        markPresenceReleasedByThisTab(uploadId);
      })
      .catch((err) => {
        releasePresence();
        reject(err);
      });
  });
};

/**
 * Query the current ownership state for an upload.
 *
 * Returns one of:
 *   - "unsupported" — Web Locks API not available; caller should treat as "unknown".
 *   - "none"        — no tab currently owns the upload.
 *   - "me"          — this tab owns it (active session or presence claim).
 *   - "other"       — another tab owns it.
 */
export const queryUploadOwnership = async (uploadId) => {
  if (!isLocksSupported() || !uploadId) {
    return "unsupported";
  }
  try {
    const snapshot = await navigator.locks.query();
    const held = snapshot.held || [];
    const baseName = lockName(uploadId);
    const presenceName = `${baseName}::presence`;

    const baseHeld = held.some((l) => l.name === baseName);
    if (baseHeld) {
      return isOwnedByThisTab(uploadId) ? "me" : "other";
    }

    const presenceHeld = held.some((l) => l.name === presenceName);
    if (presenceHeld) {
      return isPresenceClaimedByThisTab(uploadId) ? "me" : "other";
    }

    return "none";
  } catch (err) {
    console.warn("[uploadOwnership] query failed", uploadId, err);
    return "unsupported";
  }
};
